import {BreakpointObserver} from '@angular/cdk/layout';
import {DatePipe} from '@angular/common';
import {Component, inject, OnInit} from '@angular/core';
import {FormsModule} from '@angular/forms';
import {MatButtonModule} from '@angular/material/button';
import {MatDialog, MatDialogModule} from '@angular/material/dialog';
import {MatIconModule} from '@angular/material/icon';
import {MatProgressSpinnerModule} from '@angular/material/progress-spinner';
import {MatSlideToggleModule} from '@angular/material/slide-toggle';
import {MatSnackBar} from '@angular/material/snack-bar';
import {MatTableModule} from '@angular/material/table';
import {MatTooltipModule} from '@angular/material/tooltip';
import {ServiceConfigurationService, ServiceConfigurationSummary} from '../service-configuration.service';

const NARROW_SCREEN_QUERY = '(max-width: 599px)';

interface ServiceConfigurationRow {
  original: ServiceConfigurationSummary;
  stringValue: string | null;
  integerValue: number | null;
  boolValue: boolean | null;
  doubleValue: number | null;
  /** Editable text for doubleValue - a plain type="number" input can never accept a German decimal comma (it's
   * hard-locked to ".", silently dropping any "," keystroke), which is exactly how costPerTree once got typed
   * as "5,50" and saved as 550 instead of 5.5. Kept as free text so the user's comma survives; parsed into
   * doubleValue on every change (see onDoubleValueTextChange). */
  doubleValueText: string;
  saving: boolean;
}

@Component({
  selector: 'app-manage-service-configurations-dialog',
  standalone: true,
  imports: [
    DatePipe,
    FormsModule,
    MatDialogModule,
    MatTableModule,
    MatButtonModule,
    MatIconModule,
    MatProgressSpinnerModule,
    MatTooltipModule,
    MatSlideToggleModule
  ],
  templateUrl: './manage-service-configurations-dialog.component.html',
  styleUrl: './manage-service-configurations-dialog.component.scss'
})
export class ManageServiceConfigurationsDialogComponent implements OnInit {
  private readonly serviceConfigurationService = inject(ServiceConfigurationService);
  private readonly breakpointObserver = inject(BreakpointObserver);
  private readonly snackBar = inject(MatSnackBar);
  private readonly dialog = inject(MatDialog);

  private static readonly NARROW_COLUMNS = ['configName', 'boolValue'];
  private static readonly DEFAULT_COLUMNS =
    ['configName', 'stringValue', 'integerValue', 'doubleValue', 'boolValue', 'lastModified'];

  displayedColumns = ManageServiceConfigurationsDialogComponent.DEFAULT_COLUMNS;
  isNarrowScreen = false;

  rows: ServiceConfigurationRow[] = [];
  loading = true;
  errorMessage: string | null = null;

  /** Off by default so a config value cannot be changed by an accidental click during a live collection. */
  editingUnlocked = false;

  toggleEditing(): void {
    this.editingUnlocked = !this.editingUnlocked;
  }

  ngOnInit(): void {
    this.breakpointObserver.observe([NARROW_SCREEN_QUERY]).subscribe(state => {
      this.isNarrowScreen = state.breakpoints[NARROW_SCREEN_QUERY];
      this.displayedColumns = this.isNarrowScreen
        ? ManageServiceConfigurationsDialogComponent.NARROW_COLUMNS
        : ManageServiceConfigurationsDialogComponent.DEFAULT_COLUMNS;
    });
    this.loadServiceConfigurations();
  }

  loadServiceConfigurations(): void {
    this.loading = true;
    this.errorMessage = null;
    this.serviceConfigurationService.list().subscribe({
      next: configurations => {
        this.rows = configurations.map(configuration => this.toRow(configuration));
        this.loading = false;
      },
      error: () => {
        this.errorMessage = 'Konfigurationen konnten nicht geladen werden.';
        this.loading = false;
      }
    });
  }

  changedByTooltip(row: ServiceConfigurationRow): string {
    return row.original.lastModifiedByName ? `Geändert von ${row.original.lastModifiedByName}` : '';
  }

  /** customerService can only be switched on while the backendService is on - judged by its saved state, so the
   * backendService has to be switched on and saved first. */
  isLockedByBackendService(row: ServiceConfigurationRow): boolean {
    if (row.original.configName !== 'customerService') {
      return false;
    }
    const backendService = this.rows.find(existing => existing.original.configName === 'backendService');
    return backendService?.original.boolValue === false;
  }

  isDirty(row: ServiceConfigurationRow): boolean {
    return row.stringValue !== row.original.stringValue
      || row.integerValue !== row.original.integerValue
      || row.boolValue !== row.original.boolValue
      || row.doubleValue !== row.original.doubleValue;
  }

  saveRow(row: ServiceConfigurationRow): void {
    row.saving = true;
    this.serviceConfigurationService.update(row.original.id, {
      stringValue: row.stringValue,
      integerValue: row.integerValue,
      boolValue: row.boolValue,
      doubleValue: row.doubleValue
    }).subscribe({
      next: updated => {
        row.original = updated;
        row.stringValue = updated.stringValue;
        row.integerValue = updated.integerValue;
        row.boolValue = updated.boolValue;
        row.doubleValue = updated.doubleValue;
        row.doubleValueText = ManageServiceConfigurationsDialogComponent.formatDouble(updated.doubleValue);
        row.saving = false;
        this.snackBar.open('Konfiguration gespeichert.', 'Ok', {duration: 4000});
        if (updated.configName === 'backendService') {
          this.refreshAfterBackendServiceChange();
        }
      },
      error: () => {
        row.saving = false;
        this.snackBar.open('Konfiguration konnte nicht gespeichert werden.', 'Ok', {duration: 5000});
      }
    });
  }

  /** Switching the backend service off makes the backend switch customerService off too, so that row changed
   * without this dialog having touched it. Reloads silently (no spinner) and leaves rows with unsaved edits alone. */
  private refreshAfterBackendServiceChange(): void {
    this.serviceConfigurationService.list().subscribe(configurations => {
      for (const configuration of configurations) {
        const row = this.rows.find(existing => existing.original.id === configuration.id);
        if (row && !this.isDirty(row) && !row.saving) {
          this.resetRowTo(row, configuration);
        }
      }
    });
  }

  private resetRowTo(row: ServiceConfigurationRow, configuration: ServiceConfigurationSummary): void {
    row.original = configuration;
    row.stringValue = configuration.stringValue;
    row.integerValue = configuration.integerValue;
    row.boolValue = configuration.boolValue;
    row.doubleValue = configuration.doubleValue;
    row.doubleValueText = ManageServiceConfigurationsDialogComponent.formatDouble(configuration.doubleValue);
  }

  /** Double click on a text value: opens the Markdown editor. The result only lands in the row (dirty) - saving
   * stays the usual per-row action. */
  editText(row: ServiceConfigurationRow): void {
    import('../markdown-text-dialog/markdown-text-dialog.component').then(({MarkdownTextDialogComponent}) => {
      this.dialog.open(MarkdownTextDialogComponent, {
        width: '700px',
        maxWidth: '95vw',
        data: {title: row.original.niceText || row.original.configName, text: row.stringValue ?? ''}
      }).afterClosed().subscribe((text: string | undefined) => {
        if (text !== undefined) {
          row.stringValue = text;
        }
      });
    });
  }

  /** Phone only: tapping the name opens all values of the service in a dialog (the narrow table shows only the
   * switch). The dialog saves itself; the row is then synced from the result. */
  openDetails(row: ServiceConfigurationRow): void {
    if (!this.isNarrowScreen) {
      return;
    }
    import('../service-configuration-detail-dialog/service-configuration-detail-dialog.component')
      .then(({ServiceConfigurationDetailDialogComponent}) => {
        this.dialog.open(ServiceConfigurationDetailDialogComponent, {
          width: '500px',
          maxWidth: '95vw',
          data: {
            configuration: row.original,
            stringValue: row.stringValue,
            integerValue: row.integerValue,
            boolValue: row.boolValue,
            doubleValue: row.doubleValue,
            editable: this.editingUnlocked,
            lockedByBackendService: this.isLockedByBackendService(row)
          }
        }).afterClosed().subscribe((updated: ServiceConfigurationSummary | undefined) => {
          if (updated) {
            this.resetRowTo(row, updated);
            if (updated.configName === 'backendService') {
              this.refreshAfterBackendServiceChange();
            }
          }
        });
      });
  }

  resetRow(row: ServiceConfigurationRow): void {
    row.stringValue = row.original.stringValue;
    row.integerValue = row.original.integerValue;
    row.boolValue = row.original.boolValue;
    row.doubleValue = row.original.doubleValue;
    row.doubleValueText = ManageServiceConfigurationsDialogComponent.formatDouble(row.original.doubleValue);
  }

  /** Parses the free-typed doubleValueText (tolerating both "," and "." as the decimal separator) into
   * doubleValue - the actual value sent on save and compared for isDirty(). Leaves doubleValue untouched while
   * the text is mid-edit and not yet a valid number (e.g. a lone "-" or trailing ","), so a stray keystroke
   * never silently reverts it. */
  onDoubleValueTextChange(row: ServiceConfigurationRow, text: string): void {
    const normalized = text.trim().replace(',', '.');
    if (normalized === '') {
      row.doubleValue = null;
      return;
    }
    const parsed = Number(normalized);
    if (Number.isFinite(parsed)) {
      row.doubleValue = parsed;
    }
  }

  private static formatDouble(value: number | null): string {
    return value === null ? '' : String(value).replace('.', ',');
  }

  private toRow(configuration: ServiceConfigurationSummary): ServiceConfigurationRow {
    return {
      original: configuration,
      stringValue: configuration.stringValue,
      integerValue: configuration.integerValue,
      boolValue: configuration.boolValue,
      doubleValue: configuration.doubleValue,
      doubleValueText: ManageServiceConfigurationsDialogComponent.formatDouble(configuration.doubleValue),
      saving: false
    };
  }
}
